import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading, StatusBadge, PriorityDot, EmptyState, tStatus } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, FolderKanban, Download, AlertCircle, Camera } from "lucide-react";

export default function Pratiche() {
  const [list, setList] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [sp] = useSearchParams();
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [incomplete, setIncomplete] = useState(sp.get("incomplete") || "all");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { t } = useTranslation();

  useEffect(() => { http.get("/projects/statuses").then((r) => setStatuses(r.data)); }, []);
  const load = () => {
    const params = { q };
    if (status !== "all") params.status = status;
    if (incomplete !== "all") params.incomplete = incomplete;
    http.get("/projects", { params }).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [q, status, incomplete]);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="pratiche-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("pratiche.title")}</h1>
        <a href={`${http.defaults.baseURL}/export/projects`} download><Button variant="outline" className="gap-1.5"><Download className="w-4 h-4" /><span className="hidden sm:inline">CSV</span></Button></a>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="pratiche-search" placeholder={t("pratiche.search_customer")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" /></div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="filter-status" className="sm:w-52"><SelectValue placeholder={t("pratiche.filter_status")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("pratiche.all_statuses")}</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s}>{tStatus(t, s)}</SelectItem>)}</SelectContent></Select>
        <Select value={incomplete} onValueChange={setIncomplete}><SelectTrigger data-testid="filter-complete" className="sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("pratiche.complete_incomplete")}</SelectItem><SelectItem value="true">{t("pratiche.only_incomplete")}</SelectItem><SelectItem value="false">{t("pratiche.only_complete")}</SelectItem></SelectContent></Select>
      </div>

      {!list ? <Loading /> : list.length === 0 ? <EmptyState icon={FolderKanban} title={t("pratiche.empty")} hint={t("pratiche.empty_hint2")} /> : (
        <div className="space-y-2">
          {list.map((p) => (
            <Card key={p.id} data-testid="project-card" onClick={() => nav(`/pratiche/${p.id}`)} className="p-4 cursor-pointer hover:shadow-md flex items-center gap-3 border-slate-200">
              <PriorityDot priority={p.priority} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0F172A] truncate flex items-center gap-2">{p.customer_name}
                  {p.requires_operator && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                  {p.is_demo && <span className="text-[9px] bg-violet-100 text-violet-700 px-1.5 rounded">DEMO</span>}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  <span>{p.comune || "—"}</span>·<span>{p.openings?.length || 0} {t("pratiche.windows_count")}</span>
                  {p.photo_count > 0 && <><span>·</span><span className="flex items-center gap-0.5"><Camera className="w-3 h-3" />{p.photo_count}</span></>}
                  {p.missing_information?.length > 0 && <span className="text-red-500 font-medium">· {t("pratiche.missing_upper")} {p.missing_information.length}</span>}
                </div>
              </div>
              <StatusBadge status={p.status} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
